<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '~/types/types'
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'

/**
 * "Latest news" block — droppable on any page; pulls cards from the
 * public news API using the configured source + count.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const repo = usePublicSiteRepository()

const count = computed(() => Math.min(Math.max(props.data.count ?? 3, 2), 6))
const tab = computed(() => (props.data.source === 'popular' ? 'popular' : 'latest'))

const { data: response } = await useAsyncData(
  () => `news-feed-${tab.value}-${count.value}`,
  () => repo.getNews({ tab: tab.value, per_page: count.value }),
  { watch: [tab, count] }
)

const posts = computed(() => response.value?.data ?? [])

const { t } = useI18n()
const localePath = useLocalePath()
const siteDate = useSiteDate()
const dateLabel = (iso: string | null) => siteDate.dayMonth(iso)
</script>

<template>
  <div>
    <div class="mb-4 flex items-baseline justify-between gap-4">
      <h3 class="site-display text-2xl tracking-tight">
        {{ data.title || t('site.news_feed_title') }}
      </h3>
      <NuxtLink
        :to="localePath('/news')"
        class="site-text-accent text-sm font-medium"
      >
        {{ t('site.news_all') }} →
      </NuxtLink>
    </div>
    <div
      v-if="posts.length"
      class="divide-y divide-[var(--site-border)]"
    >
      <NuxtLink
        v-for="post in posts"
        :key="post.slug"
        :to="localePath(`/news/${post.slug}`)"
        class="group flex items-baseline gap-4 py-3"
      >
        <span class="site-text-accent w-14 shrink-0 text-xs font-semibold tabular-nums">
          {{ dateLabel(post.published_at) }}
        </span>
        <span class="font-medium transition group-hover:text-[var(--site-accent)]">
          {{ post.title }}
        </span>
      </NuxtLink>
    </div>
    <p
      v-else
      class="text-sm text-[var(--site-muted)]"
    >
      {{ t('site.news_feed_empty') }}
    </p>
  </div>
</template>
